import { tracker, SanitizeLevel, type Options as OpenReplayOptions } from '@openreplay/tracker';
import type { RuntimeEnv } from '../config/runtimeEnv';

export type OpenReplayFeatureConfig = {
  enabled?: boolean;
  captureConsole?: boolean;
  captureErrors?: boolean;
  captureNetwork?: boolean;
};

export type OpenReplayMetadata = {
  tenantId?: string;
  tenantName?: string;
  userId: string;
  userMarketplaceId?: string;
  username?: string;
};

type OpenReplayNetworkData =
  NonNullable<NonNullable<OpenReplayOptions['network']>['sanitizer']> extends (data: infer Data) => unknown
    ? Data
    : never;

type StartOpenReplayArgs = {
  featureConfig: OpenReplayFeatureConfig;
  runtimeEnv: RuntimeEnv;
};

const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-csrf-token']);

const SENSITIVE_QUERY_PARAMS = new Set(['access_token', 'code', 'password', 'refresh_token', 'session', 'token']);

const SENSITIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[data-openreplay-hidden]',
  '[data-sensitive]',
  '[name*="password" i]',
  '[name*="token" i]',
  '[name*="card" i]',
  '[name*="cvv" i]',
  '[name*="iban" i]',
  '[name*="document" i]',
  '[autocomplete="cc-number"]',
  '[autocomplete="cc-csc"]',
  '[data-testid*="kyc" i]',
  'img[src^="blob:"]',
  'img[src^="data:"]',
].join(',');

let isConfigured = false;
let isStarted = false;
let isStarting = false;
let startGeneration = 0;
let identifiedSignature: string | null = null;
let pendingIdentityMetadata: OpenReplayMetadata | null = null;

export const canStartOpenReplay = ({ featureConfig, runtimeEnv }: StartOpenReplayArgs): boolean => {
  return Boolean(
    featureConfig.enabled && runtimeEnv.openReplayProjectKey?.trim() && runtimeEnv.openReplayIngestPoint?.trim()
  );
};

export const sanitizeOpenReplayNetworkData = (data: OpenReplayNetworkData): OpenReplayNetworkData | null => {
  const sanitizedUrl = sanitizeUrl(data.url);

  if (!sanitizedUrl) {
    return null;
  }

  return {
    ...data,
    url: sanitizedUrl,
    request: {
      body: null,
      headers: sanitizeHeaders(data.request.headers),
    },
    response: {
      body: null,
      headers: sanitizeHeaders(data.response.headers),
    },
  };
};

export const startOpenReplay = ({ featureConfig, runtimeEnv }: StartOpenReplayArgs): void => {
  if (isStarted || isStarting || typeof window === 'undefined' || !canStartOpenReplay({ featureConfig, runtimeEnv })) {
    return;
  }

  // The tracker singleton accepts configuration exactly once per page load; later
  // calls are ignored by the SDK, so a restart reuses the existing configuration.
  if (!isConfigured) {
    tracker.configure({
      // The SDK refuses to start on a non-https origin unless this is set. Every
      // deployed environment is https, so this only ever relaxes local development.
      __DISABLE_SECURE_MODE: window.location.protocol !== 'https:',
      captureExceptions: featureConfig.captureErrors ?? true,
      consoleMethods: featureConfig.captureConsole === false ? [] : ['log', 'info', 'warn', 'error', 'debug'],
      defaultInputMode: 1,
      ingestPoint: runtimeEnv.openReplayIngestPoint?.trim(),
      network: {
        captureInIframes: false,
        capturePayload: false,
        disabled: !featureConfig.captureNetwork,
        failuresOnly: false,
        ignoreHeaders: false,
        sanitizer: sanitizeOpenReplayNetworkData,
        sessionTokenHeader: false,
      },
      obscureInputDates: true,
      obscureInputEmails: true,
      obscureInputNumbers: true,
      obscureTextEmails: true,
      obscureTextNumbers: true,
      projectKey: runtimeEnv.openReplayProjectKey?.trim() ?? '',
      domSanitizer: (node) => (node.matches(SENSITIVE_SELECTOR) ? SanitizeLevel.Hidden : SanitizeLevel.Plain),
    });
    isConfigured = true;
  }

  isStarting = true;
  const generation = startGeneration;

  void tracker
    .start()
    .then((result) => {
      if (generation !== startGeneration) {
        return;
      }

      isStarting = false;

      if (!result.success) {
        pendingIdentityMetadata = null;
        return;
      }

      isStarted = true;

      if (pendingIdentityMetadata) {
        const metadata = pendingIdentityMetadata;
        pendingIdentityMetadata = null;
        try {
          identifyTrackerUser(metadata);
        } catch {
          // Identification failure must not invalidate an otherwise active tracker.
        }
      }
    })
    .catch(() => {
      if (generation === startGeneration) {
        isStarting = false;
        pendingIdentityMetadata = null;
      }
    });
};

export const identifyOpenReplayUser = (metadata: OpenReplayMetadata): void => {
  if (isStarting) {
    pendingIdentityMetadata = metadata;
    return;
  }

  if (!isStarted || identifiedSignature === buildIdentitySignature(metadata)) {
    return;
  }

  identifyTrackerUser(metadata);
};

export const resetOpenReplayUser = (): void => {
  identifiedSignature = null;
  pendingIdentityMetadata = null;
};

export const stopOpenReplay = (): void => {
  startGeneration += 1;

  if (isStarted || isStarting) {
    tracker.stop();
  }

  isStarted = false;
  isStarting = false;
  identifiedSignature = null;
  pendingIdentityMetadata = null;
};

const buildIdentitySignature = (metadata: OpenReplayMetadata): string => JSON.stringify(metadata);

const identifyTrackerUser = (metadata: OpenReplayMetadata): void => {
  tracker.setUserID(metadata.userId);
  setOpenReplayMetadata(metadata);
  identifiedSignature = buildIdentitySignature(metadata);
};

const setOpenReplayMetadata = (metadata: OpenReplayMetadata): void => {
  Object.entries(metadata).forEach(([key, value]) => {
    if (value) {
      tracker.setMetadata(key, value);
    }
  });
};

const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADER_NAMES.has(key.toLowerCase()) ? '[redacted]' : value,
    ])
  );
};

const sanitizeUrl = (rawUrl: string): string | null => {
  try {
    const url = new URL(rawUrl, window.location.origin);

    SENSITIVE_QUERY_PARAMS.forEach((paramName) => {
      if (url.searchParams.has(paramName)) {
        url.searchParams.set(paramName, '[redacted]');
      }
    });

    return url.toString();
  } catch {
    return null;
  }
};

export const __resetOpenReplayForTests = (): void => {
  isConfigured = false;
  isStarted = false;
  isStarting = false;
  startGeneration = 0;
  identifiedSignature = null;
  pendingIdentityMetadata = null;
};
